import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaCalendarAlt, FaClock, FaExternalLinkAlt, FaTimes, FaSpinner } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';
import PageHeader from '../../components/PageHeader';
import { FullPageSkeleton } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import { toast } from '../../components/ui/Toast';
import { usePortalContent, getPortalIcon, getMeetingsPageMeta, getMeetingsTabLabel, getMeetingsEmptyTitle } from '../../portal';

const Meetings = () => {
  const { currentThemeColors } = useTheme();
  const { content: meetingsContent } = usePortalContent('meetings');
  const pageMeta = getMeetingsPageMeta(meetingsContent);
  const PageIcon = getPortalIcon(pageMeta.pageIcon);
  const modalRoot = typeof document !== 'undefined' ? document.body : null;
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/meetings/user/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMeetings(data.meetings);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        title: formData.title,
        description: formData.description,
        startTime: new Date(formData.startTime).toISOString(),
        duration: 30
      };

      const response = await fetch(`${API_BASE_URL}/api/meetings/user/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(pageMeta.createSuccessMessage);
        setShowCreateModal(false);
        setFormData({ title: '', description: '', startTime: '' });
        fetchMeetings();
      } else {
        toast.error(data.message || pageMeta.createErrorMessage);
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(pageMeta.createErrorMessage);
    } finally {
      setCreating(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const istTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const localTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return {
      date: dateStr,
      time: `${istTime} IST`,
      localTime: localTime,
      dualTime: `${istTime} IST (${localTime} Your Time)`
    };
  };

  const getMeetingStatus = (meeting) => {
    const now = new Date();
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(startTime.getTime() + meeting.duration * 60000);

    if (meeting.status === 'cancelled') return 'cancelled';
    if (now >= startTime && now <= endTime) return 'ongoing';
    if (now > endTime) return 'completed';
    return 'upcoming';
  };

  const filterMeetings = () => {
    return meetings.filter(meeting => {
      const status = getMeetingStatus(meeting);
      if (activeTab === 'upcoming') return status === 'upcoming';
      if (activeTab === 'ongoing') return status === 'ongoing';
      if (activeTab === 'past') return status === 'completed' || status === 'cancelled';
      return true;
    });
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  // Convert user's local time to IST and validate 5-8 PM IST window
  const isValidMeetingTime = (dateTimeString) => {
    if (!dateTimeString) return { valid: true };
    const selectedDate = new Date(dateTimeString);

    // Convert to IST by adding 5:30 to UTC
    const istMs = selectedDate.getTime() + (5.5 * 60 * 60 * 1000);
    const istDate = new Date(istMs);

    const istDay = istDate.getUTCDay(); // 0=Sun, 6=Sat
    const istHours = istDate.getUTCHours();

    // Check if weekday (Monday=1 to Friday=5) in IST
    if (istDay === 0 || istDay === 6) {
      return { valid: false, message: pageMeta.weekdayError };
    }

    // Check if time is between 5:00 PM (17:00) and 8:00 PM (20:00) IST
    if (istHours < 17 || istHours >= 20) {
      return { valid: false, message: pageMeta.timeWindowError };
    }

    return { valid: true };
  };

  // Get IST equivalent of a selected local datetime for display
  const getISTEquivalent = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return '';
    const istTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const istDate = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' });
    return `${istDate}, ${istTime} IST`;
  };

  useEffect(() => {
    if (!modalRoot || !showCreateModal) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showCreateModal, modalRoot]);

  const renderCreateMeetingModal = () => {
    if (!showCreateModal || !modalRoot) return null;
    return createPortal(
      <div
        className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={() => setShowCreateModal(false)}
      >
        <div
          className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-scale-in"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between gap-4">
            <div>
              <h2 className="section-title">{pageMeta.modalTitle}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {pageMeta.modalSubtitle}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(false)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleCreateMeeting} className="flex-1 overflow-y-auto p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {pageMeta.titleFieldLabel}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder={pageMeta.titlePlaceholder}
                required
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">{pageMeta.descriptionFieldLabel}</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder={pageMeta.descriptionPlaceholder}
                rows={3}
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-700 dark:text-gray-300 mb-2">
                {pageMeta.datetimeFieldLabel} <span className="text-xs font-normal text-gray-500">{pageMeta.datetimeHint}</span>
              </label>
              <input
                type="datetime-local"
                value={formData.startTime}
                onChange={e => {
                  setFormData({ ...formData, startTime: e.target.value });
                }}
                min={getMinDateTime()}
                required
                className="w-full p-3 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
              {formData.startTime && (() => {
                const validation = isValidMeetingTime(formData.startTime);
                return (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {pageMeta.istEquivalentLabel} <span className="font-medium text-dark-700 dark:text-gray-200">{getISTEquivalent(formData.startTime)}</span>
                    </p>
                    {!validation.valid && (
                      <p className="text-xs text-red-500 dark:text-red-400 font-medium">
                        {validation.message}
                      </p>
                    )}
                  </div>
                );
              })()}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{pageMeta.durationNote}</p>
            </div>


            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200"
              >
                {pageMeta.cancelButtonLabel}
              </button>
              <button
                type="submit"
                disabled={creating}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {creating ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    {pageMeta.creatingButtonLabel}
                  </>
                ) : (
                  pageMeta.createButtonLabel
                )}
              </button>
            </div>
          </form>
        </div>
      </div>,
      modalRoot
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message={pageMeta.loadingMessage} />
      </div>
    );
  }

  const getStatusBadgeStyles = (status) => {
    switch (status) {
      case 'ongoing':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'upcoming':
        return 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400';
      case 'completed':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={PageIcon}
        title={pageMeta.pageTitle}
        subtitle={pageMeta.pageSubtitle}
        action={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg sm:rounded-xl text-white text-sm font-medium sm:hover:scale-105 transition-all duration-300 shrink-0 w-full sm:w-auto"
            style={{
              background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
              boxShadow: `0 10px 15px -3px var(--glow-color)`,
            }}
          >
            <FaPlus className="w-4 h-4" /> {pageMeta.scheduleButtonLabel}
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 p-1 rounded-xl bg-gray-100 dark:bg-dark-700 w-fit opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        {['upcoming', 'ongoing', 'past'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {getMeetingsTabLabel(pageMeta, tab)}
          </button>
        ))}
      </div>

      {/* Meetings List */}
      <div className="space-y-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        {filterMeetings().length === 0 ? (
          <EmptyState
            icon={FaCalendarAlt}
            title={getMeetingsEmptyTitle(pageMeta, activeTab)}
          >
            {activeTab === 'upcoming' && (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              >
                {pageMeta.emptyScheduleButtonLabel}
              </button>
            )}
          </EmptyState>
        ) : (
          filterMeetings().map(meeting => {
            const { date, time } = formatDateTime(meeting.startTime);
            const status = getMeetingStatus(meeting);
            return (
              <div key={meeting._id} className="card p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-dark-800 dark:text-white">{meeting.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeStyles(status)}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                    {meeting.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{meeting.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1.5">
                        <FaCalendarAlt className="w-4 h-4 text-primary-500" /> {date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FaClock className="w-4 h-4 text-primary-500" /> {formatDateTime(meeting.startTime).dualTime}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FaClock className="w-4 h-4 text-primary-500" /> {meeting.duration} {pageMeta.durationSuffix}
                      </span>
                    </div>
                  </div>
                  {(status === 'upcoming' || status === 'ongoing') && meeting.joinUrl && (
                    <a 
                      href={meeting.joinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
                    >
                      <FaExternalLinkAlt className="w-4 h-4" /> {pageMeta.joinMeetingLabel}
                    </a>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {renderCreateMeetingModal()}
    </div>
  );
};

export default Meetings;
